/**
 * Wait until Swiper instance is ready
 */
function waitForSwiper(slider, callback, timeout = 5000) {
  const start = Date.now();

  function check() {
    if (slider.swiper) {
      callback(slider.swiper);
    } else if (Date.now() - start < timeout) {
      requestAnimationFrame(check);
    }
  }

  check();
}


/**
 * Build Swiper from existing product cards
 */
function buildProductSwiper(container, sliderClass, navIdPrefix) {
  const cardCount = container.children.length;
  if (cardCount === 0) return null;

  const sectionRoot = container.closest("[id^='shopify-section-']");
  const sectionId = sectionRoot?.id?.replace("shopify-section-", "") || "";

  
  const swiperSlider = document.createElement("swiper-slider");
  swiperSlider.setAttribute("data-swiper-id", "collection-products-carousel");
  swiperSlider.className = sliderClass;

  swiperSlider.dataset.swiperConfig = JSON.stringify({
    watchOverflow: true,
    navigation: {
      enabled: true,
      nextEl: sectionId
        ? `#${navIdPrefix}-next-${sectionId}`
        : `[data-${navIdPrefix}-next]`,
      prevEl: sectionId
        ? `#${navIdPrefix}-prev-${sectionId}`
        : `[data-${navIdPrefix}-prev]`,
    },
    pagination: { enabled: false },
  });

  const swiperEl = document.createElement("div");
  swiperEl.className = "swiper";

  const wrapper = document.createElement("div");
  wrapper.className = "swiper-wrapper";
  wrapper.id = container.id || "";
  wrapper.dataset.blockCount =
    container.dataset.blockCount || String(cardCount);

  while (container.firstElementChild) {
    const slide = document.createElement("div");
    slide.className = "swiper-slide";
    slide.appendChild(container.firstElementChild);
    wrapper.appendChild(slide);
  }

  swiperEl.appendChild(wrapper);
  swiperSlider.appendChild(swiperEl);

  container.parentNode.replaceChild(swiperSlider, container);

  return swiperSlider;
}


/**
 * Bind pagination UI (progress + counter + arrows)
 */
function bindProductPagination(swiper, sectionRoot, selectors) {
  const progressEl = sectionRoot.querySelector(selectors.progress);
  const counterEl = sectionRoot.querySelector(selectors.counter);
  const prevBtn = sectionRoot.querySelector(selectors.prev);
  const nextBtn = sectionRoot.querySelector(selectors.next);

  function update() {
    const visibleSlides = Array.from(swiper.slides || []).filter(sl => sl.style.display !== "none");
    const total = visibleSlides.length;

    let spv = 1;
    if (typeof swiper.params.slidesPerView === "number") {
      spv = Math.floor(swiper.params.slidesPerView);
    }
    let currentIndex = 0;
    let minOffset = Infinity;

    visibleSlides.forEach((slide, index) => {
      const offset = Math.abs(slide.offsetLeft - swiper.translate * -1);

      if (offset < minOffset) {
        minOffset = offset;
        currentIndex = index;
      }
    });

    let current = swiper.activeIndex + spv;
    current = Math.min(current, total);
    const pct = total > 0 ? (current / total) * 100 : 0;

    if (progressEl) progressEl.style.width = `${pct}%`;
    if (counterEl) {
      counterEl.textContent = `${String(current).padStart(2, "0")}/${String(total).padStart(2, "0")}`;
    }
  }

  update();
  swiper.on("slideChange transitionEnd", update);

  if (prevBtn) prevBtn.addEventListener("click", () => swiper.slidePrev());
  if (nextBtn) nextBtn.addEventListener("click", () => swiper.slideNext());
}


/**
 * Initialize Swiper
 */
function initProductCarousels(section) {

  const cardsContainer = section.querySelector(
    "[data-vp-collection-tabs-cards]"
  );
  if (!cardsContainer) return;
  if (cardsContainer.hasAttribute("data-vp-ct-defer")) {
    deferCards(section, cardsContainer);
    return;
  }
  const loader = section.querySelector(".vp-slider-loader");

  const swiperSlider = buildProductSwiper(
    cardsContainer,
    "vp-collection-tabs-cards__slider",
    "vp-product-carousel"
  );

  if (!swiperSlider) {
    if (loader) loader.classList.add("loaded"); 
    return;
  }

  waitForSwiper(swiperSlider, (swiperInstance) => {
    const sectionRoot = swiperSlider.closest("[id^='shopify-section-']");

    bindProductPagination(swiperInstance, sectionRoot, {
      progress: ".vp-product-carousel__pagination-progress",
      counter: ".vp-product-carousel__pagination-counter",
      prev: "[data-vp-product-carousel-prev]",
      next: "[data-vp-product-carousel-next]",
    });

    const tabsEl = section.querySelector("vp-collection-tabs");
    if (tabsEl) {
      activateInitialTab(tabsEl);
    }
  });
  if (loader) {
    setTimeout(() => {
      loader.classList.add("loaded");
    }, 300);
  }
}

function bodyScrolls() {
  return getComputedStyle(document.body).overflowY !== "visible" && document.body.scrollHeight > document.body.clientHeight;
}

/**
 * Cards left out of the page are fetched as this section on its own once the slider is near the
 * screen, then built exactly as if they had been there from the start.
 */
function deferCards(section, container) {
  if (container.dataset.vpCtWaiting) return;
  container.dataset.vpCtWaiting = "1";

  const id = section.closest("[id^='shopify-section-']")?.id.replace("shopify-section-", "");
  if (!id) return;

  const load = () => {
    const url = new URL(location.href);
    url.searchParams.set("section_id", id);
    fetch(url)
      .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
      .then((html) => {
        const fresh = new DOMParser()
          .parseFromString(html, "text/html")
          .querySelector("[data-vp-collection-tabs-cards]");
        if (!fresh || !container.isConnected) return;
        container.append(...fresh.children);
        container.removeAttribute("data-vp-ct-defer");
        initProductCarousels(section);
      })
      .catch(() => {});
  };

  if (!("IntersectionObserver" in window)) {
    load();
    return;
  }

  const watch = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      watch.disconnect();
      load();
    },
    // Where `body` is the scroller it clips everything off screen, so a margin on the viewport
    // never reaches the slider; the margin has to be on the scroller itself.
    { root: bodyScrolls() ? document.body : null, rootMargin: "1500px 0px" }
  );
  watch.observe(section);
}

/**
 * Activate the initial tab
 */
function activateInitialTab(tabsEl) {
  if (!tabsEl || !tabsEl.buttons?.length) return;

  const activeBtn =
    tabsEl.querySelector("button.active") || tabsEl.buttons[0];

  activeBtn.classList.add("active");

  const handle = activeBtn.dataset.collection;

  if (handle) {
    tabsEl.toggleCollection(handle);
  }
}


/**
 * Collection Tabs (Swiper-compatible)
 */
if (!customElements.get("vp-collection-tabs")) {
  class CollectionTabs extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.target = this.dataset.target;
      this.buttons = Array.from(this.querySelectorAll("button"));

      this.buttons.forEach((button) => {
        button.addEventListener("click", (event) => {
          const handle = button.dataset.collection;

          this.buttons.forEach((el) => el.classList.remove("active"));
          button.classList.add("active");

          if (handle) {
            this.toggleCollection(handle);
          }

          event.preventDefault();
        });
      });

      if (Shopify.designMode) {
        this.addEventListener("shopify:block:select", (event) => {
          const index = this.buttons.indexOf(event.target);
          if (index !== -1) {
            this.buttons[index].click();
          }
        });
      }
    }

    toggleCollection(handle) {
      const sliderRoot = document.getElementById(this.target);
      if (!sliderRoot) return;

      const swiperEl = sliderRoot.closest("swiper-slider");
      if (!swiperEl) return;

      const swiper = swiperEl.swiper;
      const slides = swiperEl.querySelectorAll(".swiper-slide");
      if (!slides.length) return;

      slides.forEach((slide) => {
        const card = slide.querySelector("[data-collection]");
        if (!card) return;

        if (card.dataset.collection === handle) {
          slide.style.display = "";
        } else {
          slide.style.display = "none";
        }
      });

      if (swiper) {
        swiper.update();
        swiper.slideTo(0);
      }
      swiper.emit("slideChange");
    }
  }

  customElements.define("vp-collection-tabs", CollectionTabs);
}


/**
 * Init on load
 */
function initAll() {
  document.querySelectorAll(".vp-collection-tabs").forEach((section) => {
    initProductCarousels(section);
  });
}


if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initAll);
} else {
  initAll();
}


/**
 * Shopify section reload support
 */
document.addEventListener("shopify:section:load", (e) => {
  if (e.target.querySelector(".vp-collection-tabs-cards")) {
    initAll();
  }
});